/**
 * PomodoroTimer - Timer con tecnica Pomodoro adaptada para ADHD
 *
 * Basado en evidencia:
 * - Sesiones cortas (25 min trabajo, 5 min descanso)
 * - Feedback visual constante del progreso
 * - Opcion de sesiones mas cortas (15 min) para dias dificiles
 * - Sin presion - puedes pausar cuando quieras
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play,
  Pause,
  RotateCcw,
  Coffee,
  Brain,
  Settings,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { cn } from '@/core/utils';
import { Button, Card } from '@/ui/primitives';
import { feedbackComplete, feedbackSuccess } from '@/services/feedback';
import { useUIStore } from '@/store';
import { trackFeatureUsed } from '@/services/achievements';

type TimerMode = 'focus' | 'break' | 'longBreak';

interface TimerPreset {
  focus: number;      // minutos
  break: number;      // minutos
  longBreak: number;  // minutos
  sessionsBeforeLong: number;
}

const DEFAULT_PRESET: TimerPreset = {
  focus: 25,
  break: 5,
  longBreak: 15,
  sessionsBeforeLong: 4,
};

const PRESETS: Record<string, TimerPreset> = {
  standard: DEFAULT_PRESET,
  short: {
    focus: 15,
    break: 3,
    longBreak: 10,
    sessionsBeforeLong: 4,
  },
  long: {
    focus: 45,
    break: 10,
    longBreak: 20,
    sessionsBeforeLong: 3,
  },
};

function getPreset(key: string): TimerPreset {
  return PRESETS[key] ?? DEFAULT_PRESET;
}

export function PomodoroTimer() {
  const preferences = useUIStore((state) => state.preferences);

  // Estado del timer
  const [preset, setPreset] = useState<keyof typeof PRESETS>('standard');
  const [mode, setMode] = useState<TimerMode>('focus');
  const [timeLeft, setTimeLeft] = useState(DEFAULT_PRESET.focus * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionsCompleted, setSessionsCompleted] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(preferences.soundEnabled);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentPreset = getPreset(preset);

  // Calcular tiempo total segun modo actual
  const getTotalTime = useCallback(() => {
    switch (mode) {
      case 'focus':
        return currentPreset.focus * 60;
      case 'break':
        return currentPreset.break * 60;
      case 'longBreak':
        return currentPreset.longBreak * 60;
    }
  }, [mode, currentPreset]);

  // Formatear tiempo como MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calcular progreso (0-100)
  const progress = ((getTotalTime() - timeLeft) / getTotalTime()) * 100;

  // Manejar fin del timer
  const handleTimerComplete = useCallback(() => {
    setIsRunning(false);

    if (mode === 'focus') {
      // Sesion de trabajo completada
      const newSessions = sessionsCompleted + 1;
      setSessionsCompleted(newSessions);
      feedbackComplete();

      // Registrar uso del timer para achievements
      trackFeatureUsed('timer');

      // Determinar siguiente modo
      if (newSessions % currentPreset.sessionsBeforeLong === 0) {
        setMode('longBreak');
        setTimeLeft(currentPreset.longBreak * 60);
      } else {
        setMode('break');
        setTimeLeft(currentPreset.break * 60);
      }
    } else {
      // Descanso completado, volver a focus
      feedbackSuccess();
      setMode('focus');
      setTimeLeft(currentPreset.focus * 60);
    }
  }, [mode, sessionsCompleted, currentPreset]);

  // Timer countdown
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleTimerComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, handleTimerComplete]);

  // Reset timer
  const handleReset = () => {
    setIsRunning(false);
    setTimeLeft(getTotalTime());
  };

  // Cambiar preset
  const handlePresetChange = (newPreset: keyof typeof PRESETS) => {
    setPreset(newPreset);
    setIsRunning(false);
    setMode('focus');
    const presetConfig = getPreset(newPreset);
    setTimeLeft(presetConfig.focus * 60);
    setSessionsCompleted(0);
  };

  // Skip to next phase
  const handleSkip = () => {
    handleTimerComplete();
  };

  // Colores segun modo
  const modeColors = {
    focus: {
      bg: 'bg-rose-900/20',
      border: 'border-rose-700',
      text: 'text-rose-400',
      progress: 'bg-rose-500',
    },
    break: {
      bg: 'bg-emerald-900/20',
      border: 'border-emerald-700',
      text: 'text-emerald-400',
      progress: 'bg-emerald-500',
    },
    longBreak: {
      bg: 'bg-blue-900/20',
      border: 'border-blue-700',
      text: 'text-blue-400',
      progress: 'bg-blue-500',
    },
  };

  const colors = modeColors[mode];

  return (
    <Card className={cn('p-6', colors.bg, colors.border)}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {mode === 'focus' ? (
              <Brain className={cn('w-5 h-5', colors.text)} />
            ) : (
              <Coffee className={cn('w-5 h-5', colors.text)} />
            )}
            <h2 className={cn('font-semibold', colors.text)}>
              {mode === 'focus' && 'Tiempo de enfoque'}
              {mode === 'break' && 'Descanso corto'}
              {mode === 'longBreak' && 'Descanso largo'}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
              title={soundEnabled ? 'Silenciar' : 'Activar sonido'}
            >
              {soundEnabled ? (
                <Volume2 className="w-4 h-4 text-slate-400" />
              ) : (
                <VolumeX className="w-4 h-4 text-slate-500" />
              )}
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
              title="Configuracion"
            >
              <Settings className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Settings panel */}
        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="pb-4 space-y-3">
                <p className="text-xs text-slate-500">Duracion de sesiones:</p>
                <div className="flex gap-2">
                  {Object.keys(PRESETS).map((key) => (
                    <button
                      key={key}
                      onClick={() => handlePresetChange(key as keyof typeof PRESETS)}
                      className={cn(
                        'flex-1 px-3 py-2 rounded-lg text-sm transition-colors',
                        preset === key
                          ? 'bg-slate-700 text-slate-100'
                          : 'bg-slate-800/50 text-slate-400 hover:bg-slate-800'
                      )}
                    >
                      {key === 'short' && '15 min'}
                      {key === 'standard' && '25 min'}
                      {key === 'long' && '45 min'}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Timer display */}
        <div className="relative flex flex-col items-center py-8">
          {/* Progress ring */}
          <div className="relative w-48 h-48">
            <svg className="w-full h-full transform -rotate-90">
              {/* Background circle */}
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-slate-800"
              />
              {/* Progress circle */}
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={2 * Math.PI * 88}
                strokeDashoffset={2 * Math.PI * 88 * (1 - progress / 100)}
                strokeLinecap="round"
                className={colors.progress}
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
            </svg>

            {/* Time display */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-5xl font-mono font-bold text-slate-100">
                {formatTime(timeLeft)}
              </span>
              <span className="text-sm text-slate-500 mt-1">
                Sesion {sessionsCompleted + 1}
              </span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="ghost"
            size="md"
            onClick={handleReset}
            disabled={timeLeft === getTotalTime() && !isRunning}
          >
            <RotateCcw className="w-4 h-4" />
          </Button>

          <Button
            variant="primary"
            size="lg"
            onClick={() => setIsRunning(!isRunning)}
            className="px-8"
          >
            {isRunning ? (
              <>
                <Pause className="w-5 h-5 mr-2" />
                Pausar
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                {timeLeft === getTotalTime() ? 'Iniciar' : 'Continuar'}
              </>
            )}
          </Button>

          <Button
            variant="ghost"
            size="md"
            onClick={handleSkip}
            title="Saltar al siguiente"
          >
            <span className="text-xs">Saltar</span>
          </Button>
        </div>

        {/* Session indicators */}
        <div className="flex justify-center gap-2">
          {Array.from({ length: currentPreset.sessionsBeforeLong }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'w-3 h-3 rounded-full transition-colors',
                i < sessionsCompleted % currentPreset.sessionsBeforeLong
                  ? colors.progress
                  : 'bg-slate-700'
              )}
            />
          ))}
        </div>

        {/* Mensaje motivacional */}
        <p className="text-center text-xs text-slate-500">
          {mode === 'focus' && 'Concentrate en una sola cosa. Todo lo demas puede esperar.'}
          {mode === 'break' && 'Levantate, estira, respira. Tu cerebro lo necesita.'}
          {mode === 'longBreak' && 'Buen trabajo. Toma un descanso real, lo mereces.'}
        </p>
      </div>
    </Card>
  );
}
